// /devices — admin device browse (data-table console).
//
// GET /v3/admin/devices?status=&model=&limit=&cursor= (FACTS PACK §3c). Filters by
// status + model; opaque forward-only cursor → client cursor-stack prev/next
// (useDeviceBrowse). next_cursor is OMITTED on the last page (normalizeCursor maps
// missing/"" → null). Row → /devices/:id. Loading / empty / error on every state
// (shared StateBlock; 403-aware).
//
// model is a free-text filter (the fleet has arbitrary model ids, e.g. ipcam-x1);
// a debounced text input commits to the browse filter.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatTimestampUs } from '../../lib/format';
import { EmptyState, ErrorState, LoadingState } from '../../components/StateBlock';
import { deviceName, deviceStatusLabel, deviceStatusTone, onlineLabel, onlineTone, recordingSummary } from './deviceModel';
// deviceStatusTone 同時驅動狀態 pill 與列首健康色點,兩處色票單一事實來源
import type { AdminDevice } from './devicesApi';
import { useDeviceBrowse } from './useDeviceBrowse';
import type { DeviceFilters } from './useDeviceBrowse';
import { listEcosystems } from '../ecosystem/ecosystemApi';
import type { Ecosystem } from '../ecosystem/ecosystemApi';
import '../_shared/shared.css';
import './devices.css';

// eco-kinio-ai 的 row display_name 是小寫 "kinio.ai";顯示端統一成品牌字 "Kinio.AI"。
function ecoLabel(e: Ecosystem): string {
  return e.ecosystem_id === 'eco-kinio-ai' ? 'Kinio.AI' : e.display_name || e.ecosystem_id;
}

/** 原生態系 cell:裝置的原生(base)生態系 + 型號 org(品牌/製造方)。比照 user browse
 *  的「原生態系」欄(restful#84)。平台生態系=neutral、其他=info。 */
function OriginEcosystemCell({ d }: { d: AdminDevice }) {
  if (!d.base_ecosystem_name && !d.base_ecosystem_id) return <span className="text-tertiary">—</span>;
  const platform = d.base_ecosystem_id === 'eco-kinio-ai';
  return (
    <span className="dv-eco-cell">
      <span className={`pill pill-${platform ? 'neutral' : 'info'}`} title={d.base_ecosystem_id}>
        {d.base_ecosystem_name || d.base_ecosystem_id}
      </span>
      {d.product_org_name && <span className="text-tertiary dv-eco-org">{d.product_org_name}</span>}
    </span>
  );
}

/** 加入的生態系 cell:裝置 base 的 effective set 減 base 的顯式聯集成員 + kinio.ai
 *  平台聯盟旗標(旗標不列舉成員)。比照 user browse 的「加入的生態系」欄(restful#84)。
 *  '—' 當無 join。 */
function JoinedEcosystemsCell({ d }: { d: AdminDevice }) {
  const joined = d.joined_ecosystems ?? [];
  const showUnion = !!d.platform_union && d.base_ecosystem_id !== 'eco-kinio-ai';
  if (joined.length === 0 && !showUnion) return <span className="text-tertiary">—</span>;
  return (
    <span className="dv-eco-joined">
      {joined.map((e) => (
        <span key={e.id} className="pill pill-info" title={e.id}>
          {e.name || e.id}
        </span>
      ))}
      {showUnion && (
        <span className="dv-eco-union-flag" title="加入 kinio.ai 平台聯盟(access-time 互通,不列舉成員)">
          <span aria-hidden="true">✓</span> 平台聯盟
        </span>
      )}
    </span>
  );
}

const STATUSES = [
  { value: '', label: '全部狀態' },
  { value: 'active', label: '正常' },
  { value: 'disabled', label: '已停用' },
  { value: 'lost', label: '已掛失' },
];

function StatusPill({ status }: { status: string | undefined }) {
  return <span className={`pill pill-${deviceStatusTone(status)}`}>{deviceStatusLabel(status)}</span>;
}

function OnlinePill({ d }: { d: AdminDevice }) {
  return <span className={`pill pill-${onlineTone(d)}`}>{onlineLabel(d)}</span>;
}

export default function DevicesPage() {
  const navigate = useNavigate();

  // committed filters (drive the hook); model is debounced from a text input
  const [status, setStatus] = useState('');
  const [modelInput, setModelInput] = useState('');
  const [modelCommitted, setModelCommitted] = useState('');
  const [ecosystem, setEcosystem] = useState('');
  const [ecosystems, setEcosystems] = useState<Ecosystem[]>([]);

  // debounce the free-text model filter (300ms)
  useEffect(() => {
    const t = setTimeout(() => setModelCommitted(modelInput.trim()), 300);
    return () => clearTimeout(t);
  }, [modelInput]);

  // ecosystem filter options (common#24 inventory); best-effort — a failure just
  // leaves "全部生態系" and never blocks the browse.
  useEffect(() => {
    let alive = true;
    listEcosystems({ limit: 200 })
      .then((r) => alive && setEcosystems(r))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const filters: DeviceFilters = useMemo(() => ({ status, model: modelCommitted, ecosystem }), [status, modelCommitted, ecosystem]);
  const browse = useDeviceBrowse(filters);

  // pass the row through router state so the detail page paints instantly, then refetches
  const open = (d: AdminDevice) => navigate(`/devices/${encodeURIComponent(d.device_id)}`, { state: { device: d } });

  const rows = browse.devices;
  const showInitialLoading = browse.loading && rows === null;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">裝置</h1>
          <p className="page-subtitle">瀏覽與搜尋裝置（依狀態／型號），點選任一列查看詳情並執行裝置處置。</p>
        </div>
        <button className="btn-ghost" onClick={() => browse.reload()} disabled={browse.loading}>
          {browse.loading ? '載入中…' : '重新整理'}
        </button>
      </div>

      {/* ---- filter toolbar：扁平化控制列(非 .card),與下方內容卡片明確分層,sticky 常駐 ---- */}
      <div className="filter-bar is-sticky">
        <label className="adm-field">
          <span className="text-secondary adm-field-label">狀態</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <label className="adm-field">
          <span className="text-secondary adm-field-label">型號</span>
          <input
            type="search"
            placeholder="型號關鍵字，如 ipcam-x1"
            aria-label="依型號篩選"
            value={modelInput}
            onChange={(e) => setModelInput(e.target.value)}
          />
        </label>
        <label className="adm-field">
          <span className="text-secondary adm-field-label">生態系</span>
          <select value={ecosystem} onChange={(e) => setEcosystem(e.target.value)} aria-label="依生態系篩選">
            <option value="">全部生態系</option>
            {ecosystems.map((e) => (
              <option key={e.ecosystem_id} value={e.ecosystem_id}>
                {ecoLabel(e)}
              </option>
            ))}
          </select>
        </label>
        <div className="adm-toolbar-spacer" />
        {/* 常駐渲染避免版面隨輸入跳動;無篩選值時單純 disabled */}
        <button
          className="btn-ghost"
          disabled={!status && !modelInput && !ecosystem}
          onClick={() => {
            setStatus('');
            setModelInput('');
            setEcosystem('');
          }}
        >
          清除篩選
        </button>
      </div>

      {/* ---- states ---- */}
      {showInitialLoading && <LoadingState label="載入裝置清單…" />}

      {browse.error != null && !browse.loading && <ErrorState error={browse.error} onRetry={() => browse.reload()} />}

      {browse.error == null && rows && rows.length === 0 && (
        <EmptyState
          title="沒有符合條件的裝置"
          detail="試著放寬狀態／型號／生態系篩選；目前環境的裝置數量很少。"
          action={
            (status || modelCommitted || ecosystem) ? (
              <button
                className="btn-ghost btn-sm"
                onClick={() => {
                  setStatus('');
                  setModelInput('');
                  setEcosystem('');
                }}
              >
                清除篩選
              </button>
            ) : undefined
          }
        />
      )}

      {browse.error == null && rows && rows.length > 0 && (
        <>
          <div className="table-wrap adm-devices-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>裝置</th>
                  <th>型號</th>
                  <th>基礎生態系</th>
                  <th>加入生態系</th>
                  <th>韌體</th>
                  <th>狀態</th>
                  <th>連線</th>
                  <th>錄影</th>
                  <th>更新時間</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr
                    key={d.device_id}
                    className="clickable"
                    tabIndex={0}
                    onClick={() => open(d)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') open(d);
                    }}
                  >
                    <td className="adm-cell-strong adm-device-name-cell">
                      {/* 健康色點:一眼看出這一列狀態好壞,不必橫掃到最右「連線」欄 */}
                      <span className={`adm-health-dot tone-${deviceStatusTone(d.status)}`} aria-hidden="true" />
                      <span className="adm-device-name">{deviceName(d)}</span>
                      {d.display_name && d.display_name !== d.device_id && (
                        <div className="text-tertiary adm-mono" style={{ fontSize: 'var(--font-size-xs)' }}>
                          {d.device_id}
                        </div>
                      )}
                    </td>
                    <td className="text-secondary">{d.model || '—'}</td>
                    <td>
                      <OriginEcosystemCell d={d} />
                    </td>
                    <td>
                      <JoinedEcosystemsCell d={d} />
                    </td>
                    <td className="adm-mono">{d.firmware_version || '—'}</td>
                    <td>
                      <StatusPill status={d.status} />
                    </td>
                    <td>
                      <OnlinePill d={d} />
                    </td>
                    <td className="text-secondary adm-nowrap">{recordingSummary(d)}</td>
                    <td className="adm-mono">{formatTimestampUs(d.updated_at, { second: undefined })}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* 單一 sticky pager footer:合併原本表格上方「第 N 頁」與下方頁碼,只留一處 */}
            <div className="adm-devices-pager">
              <span className="text-secondary adm-devices-pager-count">本頁 {rows.length} 筆</span>
              <div className="adm-devices-pager-nav">
                <button className="btn-ghost btn-sm" onClick={browse.prev} disabled={!browse.hasPrev || browse.loading}>
                  ← 上一頁
                </button>
                <span className="adm-mono adm-devices-pager-page">第 {browse.pageIndex + 1} 頁</span>
                <button className="btn-ghost btn-sm" onClick={browse.next} disabled={!browse.hasNext || browse.loading}>
                  下一頁 →
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
